// src/services/api.js
import axios from 'axios'
import AppURLs from '../config/urls'

class ApiService {
  constructor() {
    // Create axios instance with 10-minute timeout for heavy queries/reports
    this.api = axios.create({
      baseURL: AppURLs.API,
      timeout: 600000, // 10 minutes timeout (600,000 ms) for long queries/reports
      headers: {
        'Content-Type': 'application/json',
      }
    })

    // Request interceptor to add auth token and bust browser cache on GET requests
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('tezlaa-access-token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        if (config.method === 'get') {
          config.params = { ...config.params, _t: Date.now() }
          config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
          config.headers['Pragma'] = 'no-cache'
        }

        // Basic debug logging in development only
        if (import.meta.env.DEV) {
          console.log('🚀 API Request:', config.method?.toUpperCase(), config.url)
        }

        return config
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error('❌ Request Error:', error)
        }
        return Promise.reject(error)
      }
    )

    // Response interceptor for handling errors
    this.api.interceptors.response.use(
      (response) => {
        // Only log in development
        if (import.meta.env.DEV) {
          console.log('✅ API Response:', response.config.url, response.status)
        }
        return response.data
      },
      async (error) => {
        // Only log detailed errors in development
        if (import.meta.env.DEV) {
          console.error('❌ API Error:', error.config?.url, error.response?.status, error.response?.data?.message || error.message)
        }

        const originalRequest = error.config

        // Handle 401 errors (token expired) - but avoid refresh loops
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/refresh-tokens')) {
          originalRequest._retry = true
          
          if (import.meta.env.DEV) {
            console.log('🔄 Attempting token refresh for URL:', originalRequest.url)
            console.log('🔍 Current tokens exist:', {
              access: !!localStorage.getItem('tezlaa-access-token'),
              refresh: !!localStorage.getItem('tezlaa-refresh-token')
            })
          }
          
          try {
            // Check if we have a refresh token before attempting refresh
            const refreshToken = localStorage.getItem('tezlaa-refresh-token')
            if (!refreshToken) {
              console.error('❌ No refresh token available')
              if (import.meta.env.DEV) {
                console.log('⚠️ Would normally trigger logout here, but debugging is enabled')
                return Promise.reject(new Error('No refresh token available - debugging mode'))
              }
              this.handleAuthFailure()
              return Promise.reject(new Error('No refresh token available'))
            }

            // Try to refresh token
            const newToken = await this.refreshToken()
            if (import.meta.env.DEV) {
              console.log('✅ Token refreshed successfully')
            }
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return this.api(originalRequest)
          } catch (refreshError) {
            if (import.meta.env.DEV) {
              console.error('❌ Token refresh failed:', refreshError.message)
              console.log('🔍 Refresh error details:', {
                status: refreshError.response?.status,
                url: originalRequest.url,
                message: refreshError.response?.data?.message
              })
            }
            // Only logout if refresh actually failed, not on network errors
            if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
              if (import.meta.env.DEV) {
                console.log('⚠️ Would normally trigger logout here, but debugging is enabled')
                return Promise.reject(new Error('Auth refresh failed - debugging mode'))
              }
              this.handleAuthFailure()
            } else if (import.meta.env.DEV) {
              console.warn('⚠️ Network error during refresh, not logging out')
            }
            return Promise.reject(refreshError)
          }
        }

        // Handle other errors - don't logout on network errors
        if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED' || !error.response) {
          console.warn('⚠️ Network error detected, not triggering logout')
          const errorMessage = 'Network error. Please check your connection.'
          const networkError = new Error(errorMessage)
          networkError.response = error.response
          return Promise.reject(networkError)
        }

        // Handle other HTTP errors - preserve the exact backend error message
        const errorMessage = error.response?.data?.message || error.message || 'An error occurred'
        const apiError = new Error(errorMessage)
        apiError.response = error.response
        apiError.status = error.response?.status
        return Promise.reject(apiError)
      }
    )
  }

  // Refresh token method
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('tezlaa-refresh-token')
      
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      if (import.meta.env.DEV) {
        console.log('🔄 Sending refresh token request...')
      }

      // Use direct axios call to avoid interceptor loops
      const response = await axios.post(`${AppURLs.API}${AppURLs.AUTH.REFRESH}`, {
        refreshToken
      }, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.data.success && response.data.data?.tokens) {
        const newAccessToken = response.data.data.tokens.access.token
        const newRefreshToken = response.data.data.tokens.refresh.token
        
        localStorage.setItem('tezlaa-access-token', newAccessToken)
        localStorage.setItem('tezlaa-refresh-token', newRefreshToken)
        
        if (import.meta.env.DEV) {
          console.log('✅ Tokens updated successfully')
        }
        return newAccessToken
      } else {
        if (import.meta.env.DEV) {
          console.error('❌ Invalid refresh response structure:', response.data)
        }
        throw new Error('Token refresh failed - invalid response')
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('❌ Refresh token error:', error.message, error.response?.status)
      }
      
      // Only clear tokens if it's actually an auth failure
      if (error.response?.status === 401 || error.response?.status === 403) {
        this.handleAuthFailure()
      }
      
      throw error
    }
  }

  // Handle authentication failure
  handleAuthFailure() {
    if (import.meta.env.DEV) {
      console.log('🚪 Handling authentication failure...')
      console.log('🔍 Current URL:', window.location?.pathname)
    }
    
    // Clear tokens
    localStorage.removeItem('tezlaa-access-token')
    localStorage.removeItem('tezlaa-refresh-token')
    localStorage.removeItem('tezlaa-user')
    localStorage.removeItem('tezlaa-selected-branch')
    
    // Show a toast notification instead of immediate redirect
    if (typeof window !== 'undefined' && window.location) {
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        // Add a small delay to allow any pending UI updates
        setTimeout(() => {
          window.location.href = '/login'
        }, 1000)
        
        // Show notification if toast is available
        if (window.toast) {
          window.toast.error('Session expired. Please login again.', {
            duration: 3000,
            position: 'top-right'
          })
        }
      }
    }
  }

  // Generic GET request
  async get(endpoint, params = {}, config = {}) {
    try {
      const queryParams = params && params.params ? params.params : params
      const requestOptions = typeof config === 'object' ? { params: queryParams, ...config } : { params: queryParams }
      const response = await this.api.get(endpoint, requestOptions)
      return response
    } catch (error) {
      console.error('GET request failed:', error)
      throw error
    }
  }

  // Generic POST request
  async post(endpoint, data = {}) {
    try {
      const response = await this.api.post(endpoint, data)
      return response
    } catch (error) {
      console.error('POST request failed:', error)
      throw error
    }
  }

  // Generic PUT request
  async put(endpoint, data = {}) {
    try {
      const response = await this.api.put(endpoint, data)
      return response
    } catch (error) {
      console.error('PUT request failed:', error)
      throw error
    }
  }

  // Generic PATCH request
  async patch(endpoint, data = {}) {
    try {
      console.log('🔧 PATCH request data:', JSON.stringify(data, null, 2))
      console.log('🔧 Order items in PATCH:', data.orderItems?.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        quantityType: typeof item.quantity
      })))
      
      const response = await this.api.patch(endpoint, data)
      return response
    } catch (error) {
      console.error('PATCH request failed:', error)
      throw error
    }
  }

  // Generic DELETE request
  async delete(endpoint) {
    try {
      const response = await this.api.delete(endpoint)
      return response
    } catch (error) {
      console.error('DELETE request failed:', error)
      throw error
    }
  }

  // Upload file
  async upload(endpoint, formData) {
    try {
      const response = await this.api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      })
      return response
    } catch (error) {
      console.error('Upload request failed:', error)
      throw error
    }
  }

  // Replace URL parameters (e.g., :id with actual id)
  replaceUrlParams(endpoint, params) {
    let url = endpoint
    Object.keys(params).forEach(key => {
      url = url.replace(`:${key}`, params[key])
    })
    return url
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('tezlaa-access-token')
    const user = localStorage.getItem('tezlaa-user')
    return !!(token && user)
  }

  // Get current user
  getCurrentUser() {
    const userData = localStorage.getItem('tezlaa-user')
    return userData ? JSON.parse(userData) : null
  }

  // Get access token
  getAccessToken() {
    return localStorage.getItem('tezlaa-access-token')
  }
}

export default new ApiService()